import { useEffect, useMemo, useState } from 'react';

import { numberWithPlusSignByMaxCount } from '@common/utils';
import {
  CLIENT_TO_SERVER_TAB_TYPE_MAP,
  getTabDotKey,
  PdpTabListItem,
  SERVER_TAB_TYPE_MAP,
  TabType,
} from '@domains/pdp-common';

interface CreateTabListParams {
  review_total_count: number;
  has_size_tab: boolean;
  has_recommend_tab: boolean;
  has_beauty_ingredient_tab: boolean;
  /** 서버에서 받은 탭 리스트 (BFF) */
  server_tab_list?: PdpTabListItem[] | null;
  /** 현재 활성화된 탭 (활성화된 탭은 레드닷 즉시 숨김) */
  activeTabType?: TabType;
}

export interface ProductTab {
  tabType: TabType;
  label: string;
  secondaryText?: string;
  /** 레드닷 표시 여부 (서버 dot_info 존재 + 로컬스토리지 미숨김 + 비활성 탭) */
  hasNotification?: boolean;
}

/**
 * 서버 탭 데이터에서 레드닷 표시 여부를 판별합니다.
 * 서버에서 dot_info가 있고, 로컬스토리지에 숨김 처리되지 않은 경우에만 true
 * activeTabType이 변경되면 해당 탭의 레드닷을 즉시 숨김 처리
 */
const useTabNotificationState = (serverTabList?: PdpTabListItem[] | null, activeTabType?: TabType) => {
  const [notificationMap, setNotificationMap] = useState<Record<string, boolean>>({});

  // 초기 로드 시 로컬스토리지 기반으로 notificationMap 생성
  useEffect(() => {
    if (!serverTabList?.length) {
      setNotificationMap({});
      return;
    }

    const map: Record<string, boolean> = {};
    serverTabList.forEach((item) => {
      const { tab } = item;
      const tabType = SERVER_TAB_TYPE_MAP[tab.type];
      if (tabType === undefined) return;

      // dot_info가 있는지 확인
      const hasDotInfo = !!tab.dot_info;
      if (!hasDotInfo) {
        map[tab.type] = false;
        return;
      }

      // 로컬스토리지에서 숨김 여부 확인
      try {
        const key = getTabDotKey(tab.type);
        const isHidden = localStorage.getItem(key) === 'true';
        map[tab.type] = !isHidden;
      } catch {
        map[tab.type] = true; // 로컬스토리지 접근 실패 시 표시
      }
    });

    setNotificationMap(map);
  }, [serverTabList]);

  // 탭 활성화 시 해당 탭의 레드닷 즉시 숨김
  useEffect(() => {
    if (activeTabType === undefined) return;

    const serverTabType = CLIENT_TO_SERVER_TAB_TYPE_MAP[activeTabType];
    if (!serverTabType) return;

    // 해당 탭에 레드닷이 표시 중이면 즉시 숨김
    if (notificationMap[serverTabType]) {
      setNotificationMap((prev) => ({
        ...prev,
        [serverTabType]: false,
      }));
    }
  }, [activeTabType, notificationMap]);

  return notificationMap;
};

export const useProductTabList = ({
  review_total_count,
  has_size_tab,
  has_recommend_tab,
  has_beauty_ingredient_tab,
  server_tab_list,
  activeTabType,
}: CreateTabListParams): ProductTab[] => {
  const notificationMap = useTabNotificationState(server_tab_list, activeTabType);

  const product_detail_info_title = '상품정보';
  const size_guide_title = '사이즈';
  const { label: review_title, secondaryText: review_secondary_text } = useReviewTabLabelAndSecondaryText(
    review_total_count,
    has_recommend_tab,
  );
  const recommend_title = '추천';
  const contact_title = '문의';
  const beauty_ingredient_title = '성분';

  return useMemo((): ProductTab[] => {
    // 서버 탭 리스트가 있으면 서버 데이터 기반으로 생성
    if (server_tab_list?.length) {
      const mappedTabs: ProductTab[] = [];
      server_tab_list.forEach((item) => {
        const { tab } = item;
        const tabType = SERVER_TAB_TYPE_MAP[tab.type] as TabType | undefined;
        if (tabType === undefined) return;

        mappedTabs.push({
          tabType,
          label: tab.title.text,
          secondaryText: tab.sub_title?.text,
          hasNotification: notificationMap[tab.type] ?? false,
        });
      });
      return mappedTabs;
    }

    // fallback: 기존 클라이언트 로직
    const tab_list: ProductTab[] = [
      {
        tabType: TabType.PRODUCT_DETAIL_INFO,
        label: product_detail_info_title,
      },
    ];

    if (has_size_tab) {
      tab_list.push({
        tabType: TabType.SIZE_INFO,
        label: size_guide_title,
      });
    }

    if (has_beauty_ingredient_tab) {
      tab_list.push({
        tabType: TabType.BEAUTY_INGREDIENT,
        label: beauty_ingredient_title,
      });
    }

    tab_list.push({
      tabType: TabType.REVIEW,
      label: review_title,
      secondaryText: review_secondary_text,
    });

    if (has_recommend_tab) {
      tab_list.push({
        tabType: TabType.RECOMMEND,
        label: recommend_title,
      });
    }

    return [
      ...tab_list,
      {
        tabType: TabType.CONTACT,
        label: contact_title,
      },
    ];
  }, [
    server_tab_list,
    notificationMap,
    product_detail_info_title,
    has_size_tab,
    has_beauty_ingredient_tab,
    review_title,
    review_secondary_text,
    has_recommend_tab,
    contact_title,
    size_guide_title,
    beauty_ingredient_title,
    recommend_title,
  ]);
};

const useReviewTabLabelAndSecondaryText = (reviewTotalCount: number, hasRecommendTab: boolean) => {
  const reviewTitle = '리뷰';
  const secondaryText = numberWithPlusSignByMaxCount(reviewTotalCount, hasRecommendTab ? 99999 : 9999);

  return {
    label: reviewTitle,
    secondaryText,
  };
};
